import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import {
  sexualHealthFields,
  menstrualCycleFields,
} from "../../stories/Forms/fields";
import {
  shSchema,
  menstrualSchema,
} from "../../stories/Forms/validationSchema";
import { Row, Col, Spinner } from "react-bootstrap";
import DynamicForm from "../../stories/Forms/DynamicForm";
import {
  assessmentStatusUrl,
  assessmentValidateUrl,
  phlebotomyAssessmentStatusUrl,
} from "../../utils/constants";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import useApis from "../../services/useAPI";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import { XSquare } from "react-bootstrap-icons";
import { useStepDataProtection } from "./useStepDataProtection";
import {
  calculateProgressSteps,
  updateProgressAfterCompletion,
} from "../../utils/utils";

let submissionData;

/**
 * Component to handle Sexual Health and Menstrual Cycle assessments.
 * Renders a dynamic form based on the type of test (sexual health or hormone-related).
 */

// Helper function to build sexual health data
const buildSexualHealthData = (values) => {
  const isNotActive = values?.sexually_active === "No";
  const hasNoUnprotectedSex = values?.had_unprotected_sex === "No";
  const hasNoExposure = values?.potential_sti_exposure === "No";
  const hasStiSymptoms = values?.sti_symptoms === "No";
  const otherStiExposure =
    Array.isArray(values?.potential_sti_exposure_type) &&
    values?.potential_sti_exposure_type.includes("Other");

  return {
    sexually_active: values?.sexually_active || "",
    partners: isNotActive ? "" : values?.partners || "",
    recent_sex: isNotActive ? "" : values?.recent_sex || "",
    had_unprotected_sex: isNotActive ? "" : values?.had_unprotected_sex || "",
    unprotected_sex_type:
      isNotActive || hasNoUnprotectedSex
        ? []
        : values?.unprotected_sex_type || [],
    sti_diagnosed: isNotActive ? "" : values?.sti_diagnosed || "",
    sti_symptoms: isNotActive ? "" : values?.sti_symptoms || "",
    sti_symptoms_description:
      isNotActive || hasStiSymptoms
        ? ""
        : values?.sti_symptoms_description || "",
    potential_sti_exposure: isNotActive
      ? ""
      : values?.potential_sti_exposure || "",
    potential_sti_exposure_type:
      isNotActive || hasNoExposure
        ? []
        : values?.potential_sti_exposure_type || [],
    other_sti_exposure_type:
      isNotActive || hasNoExposure || !otherStiExposure
        ? ""
        : values?.other_sti_exposure_type || "",
  };
};

// Helper function to build menstrual data
const buildMenstrualData = (values) => ({
  cycle_length: values?.cycle_length,
  day_of_cycle: values?.day_of_cycle,
  contraception: values?.contraception,
});

// Helper function to prepare submission data
const prepareSubmissionData = (isShTest, isHormoneTest, values) => {
  if (isShTest) {
    return {
      sexual_health: buildSexualHealthData(values),
      step: "sexual_health",
    };
  }

  if (isHormoneTest) {
    return {
      menstrual: buildMenstrualData(values),
      step: "menstrual",
    };
  }

  return null;
};

// Helper function to get saved sexual health data
const getSavedSexualHealthData = (authData, apiData) => ({
  sexually_active:
    authData?.sexual_health?.sexually_active ||
    apiData?.assessment_values?.sexual_health?.sexually_active ||
    "",
  partners:
    authData?.sexual_health?.partners ||
    apiData?.assessment_values?.sexual_health?.partners ||
    "",
  recent_sex:
    authData?.sexual_health?.recent_sex ||
    apiData?.assessment_values?.sexual_health?.recent_sex ||
    "",
  had_unprotected_sex:
    authData?.sexual_health?.had_unprotected_sex ||
    apiData?.assessment_values?.sexual_health?.had_unprotected_sex ||
    "",
  unprotected_sex_type:
    (authData?.sexual_health?.unprotected_sex_type ||
      apiData?.assessment_values?.sexual_health?.unprotected_sex_type ||
      []
    ).map(v => v ? v.split(" ")[0] : v),
  sti_diagnosed:
    authData?.sexual_health?.sti_diagnosed ||
    apiData?.assessment_values?.sexual_health?.sti_diagnosed ||
    "",
  sti_symptoms:
    authData?.sexual_health?.sti_symptoms ||
    apiData?.assessment_values?.sexual_health?.sti_symptoms ||
    "",
  sti_symptoms_description:
    authData?.sexual_health?.sti_symptoms_description ||
    apiData?.assessment_values?.sexual_health?.sti_symptoms_description ||
    "",
  potential_sti_exposure:
    authData?.sexual_health?.potential_sti_exposure ||
    apiData?.assessment_values?.sexual_health?.potential_sti_exposure ||
    "",
  potential_sti_exposure_type:
    authData?.sexual_health?.potential_sti_exposure_type ||
    apiData?.assessment_values?.sexual_health?.potential_sti_exposure_type ||
    [],
  other_sti_exposure_type:
    authData?.sexual_health?.other_sti_exposure_type ||
    apiData?.assessment_values?.sexual_health?.other_sti_exposure_type ||
    "",
});

// Helper function to get saved menstrual data
const getSavedMenstrualData = (authData, apiData) => ({
  cycle_length:
    authData?.menstrual?.cycle_length ||
    apiData?.assessment_values?.menstrual?.cycle_length,
  day_of_cycle:
    authData?.menstrual?.day_of_cycle ||
    apiData?.assessment_values?.menstrual?.day_of_cycle,
  contraception:
    authData?.menstrual?.contraception ||
    apiData?.assessment_values?.menstrual?.contraception,
});

// Helper function to prepare saved data
const prepareSavedData = (isShTest, isHormoneTest, authData, apiData) => {
  if (isShTest) {
    return {
      sexual_health: getSavedSexualHealthData(authData, apiData),
      step: "sexual_health",
    };
  }

  if (isHormoneTest) {
    return {
      menstrual: getSavedMenstrualData(authData, apiData),
      step: "menstrual",
    };
  }

  return null;
};

// Helper function to determine test type
const determineTestType = (testKitType) => {
  const isSexTest = testKitType?.includes("sex");
  const isHormone =
    testKitType?.includes("menopause") || testKitType?.includes("polycystic");

  return { isSexTest, isHormone };
};

function SexualHealth() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { test_kit_id, bid, test_type } = useParams();
  const [isShTest, setIsShTest] = useState();
  const [isHormoneTest, setIsHormoneTest] = useState();
  const [data, setData] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [is_assessment_completed, setIs_assessment_completed] = useState(false);
  const formId = location.pathname.split("/").at(-1);
  const { setCompletedSteps, setIsCompleted, setStep } = auth;
  const hasFetchedRef = useRef(false);
  const isPhlebotomy = location.pathname.includes("boots");
  const { callApi, resultGet, resultPost, serverError } = useApis();

  useStepDataProtection(
    auth?.allStepData,
    resultGet,
    test_kit_id,
    location,
    navigate,
    auth?.showToast,
  );

  const handleAssessmentSubmit = (values) => {
    // Remove keys with undefined values
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        delete values[key];
      }
    });

    submissionData = prepareSubmissionData(isShTest, isHormoneTest, values);
    const completedProgressSteps = updateProgressAfterCompletion(
      resultGet,
      resultGet?.test_kit_details?.test_kit_type,
      location.pathname,
    );
    auth.setProgressSteps(completedProgressSteps);

    if (submissionData) {
      callApi(
        {
          user_id: JSON.parse(localStorage.getItem("user_id")),
          ...submissionData,
        },
        `${assessmentValidateUrl}`,
        "POST",
        true,
      );
    }

    setData(submissionData);
    auth.setAllStepData((prevStep) => ({ ...prevStep, ...submissionData }));
    setIsCompleted(Number.parseInt(formId) + 1);
    setCompletedSteps((prevSteps) => prevSteps + 1);
    setStep((prevStep) => prevStep + 1);
  };

  useEffect(() => {
    if (resultGet) {
      const newProgressSteps = calculateProgressSteps(
        resultGet,
        resultGet?.test_kit_details?.test_kit_type,
        location.pathname,
      );
      auth.setProgressSteps(newProgressSteps);
    }
  }, [resultGet, location.pathname]);

  // Initial API call
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    if (isPhlebotomy && test_type && bid) {
      callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
      return;
    }
    if (!isPhlebotomy && !test_type && !bid && test_kit_id) {
      localStorage.setItem("test_kit_id", location?.state?.test_kit_id);
      callApi(null, `${assessmentStatusUrl}${test_kit_id}`, "GET", true);
    }
  }, []);

  // Handle API result and set test type
  useEffect(() => {
    if (!resultGet) return;

    if (!auth?.allStepData) {
      auth.setAllStepData(resultGet?.assessment_values);
    }

    const testKitType = resultGet?.test_kit_details?.test_kit_type;
    const { isSexTest, isHormone } = determineTestType(testKitType);

    setIsShTest(isSexTest);
    setIsHormoneTest(isHormone);
  }, [resultGet]);

  // Update spinner state
  useEffect(() => {
    setShowSpinner(!isShTest && !isHormoneTest);
  }, [isShTest, isHormoneTest]);

  // Prepare saved data
  useEffect(() => {
    if (!resultGet) return;

    submissionData = prepareSavedData(
      isShTest,
      isHormoneTest,
      auth.allStepData,
      resultGet,
    );
    setData(submissionData);
  }, [resultGet, isHormoneTest, isShTest]);


useEffect(() => {
  if (!resultGet) {
    setShowSpinner(true);
    return;
  }

  setShowSpinner(false);

  if (!auth?.allStepData) {
    auth.setAllStepData(resultGet?.assessment_values);
  }

  const testKitType = resultGet?.test_kit_details?.test_kit_type;
  const { isSexTest, isHormone } = determineTestType(testKitType);

  setIsShTest(isSexTest);
  setIsHormoneTest(isHormone);

  // Guard: redirect standard tests away from /4
  if (testKitType && !isSexTest && !isHormone) {
    navigate(`/assessment/${test_kit_id}/5`, { replace: true });
  }

  if (testKitType && isPhlebotomy) {  
    navigate(`/bloods/boots/assessment/${bid}/5`, { replace: true });
  }

}, [resultGet]);

  // Handle successful POST
  useEffect(() => {
    if (!resultPost) return;

    setIsCompleted(Number.parseInt(formId));
    setCompletedSteps(Number.parseInt(formId) + 1);
    setStep(Number.parseInt(formId));
    navigate(`/assessment/${test_kit_id}/${Number.parseInt(formId) + 1}`, {
      state: { ...auth.allStepData },
    });
  }, [resultPost]);

  // Handle errors and assessment status
  useEffect(() => {
    setIs_assessment_completed(resultGet?.is_assessment_completed);

    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [resultGet, serverError]);

  if (showSpinner) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  return (
    <div className="row start-assessment-page">
      <div className="col-md-12">
        <StepProgressBar
          steps={auth.progressSteps.totalSteps}
          currentStep={auth.progressSteps.currentStep}
          completedSteps={auth.progressSteps.completedSteps}
          isCompleted={auth.progressSteps.completedSteps}
          is_assessment_completed={false}
          isLast={true}
        />
        {(isShTest || isHormoneTest) && (
          <div className="container-fluid">
            <Row className="justify-content-center">
              <Col md={12} className="p-0">
                <div className="mt-5">
                  <h1 className="mb-4">
                    {isShTest ? "Sexual health" : "Menstrual cycle"}
                  </h1>
                  <div className="assessment-form sexual-health-form">
                    <DynamicForm
                      fields={
                        isShTest ? sexualHealthFields : menstrualCycleFields
                      }
                      validationSchema={isShTest ? shSchema : menstrualSchema}
                      blockRefresh={true}
                      modify={true}
                      initialValues={
                        isShTest
                          ? data?.sexual_health || {
                              ...resultGet?.assessment_values?.sexual_health,
                            }
                          : data?.menstrual || {
                              ...resultGet?.assessment_values?.menstrual,
                            }
                      }
                      onSubmit={handleAssessmentSubmit}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
}

export default SexualHealth;
